import React, { useCallback, useEffect, useState } from "react";
import { Bookmark, Trash2, Save, Loader2 } from "lucide-react";
import { listSteps, updateSteps, deleteSteps } from "../../lib/uploadsApi";
import { useRole } from "../../lib/role.jsx";
import { btn, when } from "../pipeline/Layer";
import { cn } from "../../lib/utils";

function StepCard({ s, meta, canEdit, onSaved, onToast }) {
  const [rules, setRules] = useState(s.rules);
  const [keep, setKeep] = useState(s.fixes.map((_, i) => i));
  const [busy, setBusy] = useState(null);
  const dirty = JSON.stringify(rules) !== JSON.stringify(s.rules) || keep.length !== s.fixes.length;
  const run = async (k, fn, msg) => { setBusy(k); try { await fn(); onToast(msg); await onSaved(); } catch (e) { onToast(e.message); } finally { setBusy(null); } };
  return (
    <div className="border border-border rounded-sm p-3 space-y-2.5" data-testid={`steps-${s.type}`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="font-semibold text-[13px] flex-1">{s.label}</div>
        <span className="text-[11px] text-muted font-mono-tabular">saved by {s.saved_by} · {when(s.saved_at)} · from {s.from_file}</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(meta.rules).map(([k, l]) => (
          <label key={k} className={cn("inline-flex items-center gap-1.5 h-7 px-2 rounded-sm border text-[11.5px]", rules[k] ? "border-primary/50 bg-primary/5" : "border-border text-muted")}>
            <input type="checkbox" data-testid={`steps-${s.type}-rule-${k}`} disabled={!canEdit} checked={!!rules[k]} onChange={e => setRules(r => ({ ...r, [k]: e.target.checked }))} />{l}</label>))}
      </div>
      {s.fixes.length > 0 && <div className="space-y-1">{s.fixes.map((f, i) => (
        <label key={i} className={cn("flex items-start gap-2 text-[12px]", !keep.includes(i) && "opacity-50 line-through")}>
          <input type="checkbox" data-testid={`steps-${s.type}-fix-${i}`} disabled={!canEdit} checked={keep.includes(i)} onChange={e => setKeep(x => (e.target.checked ? [...x, i] : x.filter(y => y !== i)))} className="mt-0.5" />
          <span><span className="font-mono-tabular">{meta.fields[f.field] || f.field}: {f.find.join(", ")} → {f.replace ?? "blank"}</span>{f.reason && <span className="text-muted"> · {f.reason}</span>}</span></label>))}</div>}
      {canEdit && <div className="flex gap-2">
        <button data-testid={`steps-${s.type}-save`} disabled={!dirty || !!busy} onClick={() => run("save", () => updateSteps(s.type, rules, keep), `Saved steps for ${s.label} updated.`)} className={cn(btn, "h-8 border border-primary/60 text-primary")}>
          {busy === "save" ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}Save changes</button>
        <button data-testid={`steps-${s.type}-delete`} disabled={!!busy} onClick={() => window.confirm(`Remove the saved steps for ${s.label}?`) && run("del", () => deleteSteps(s.type), `Saved steps for ${s.label} removed.`)} className={cn(btn, "h-8 border border-border hover:border-critical hover:text-critical")}>
          <Trash2 size={12} />Remove</button>
      </div>}
    </div>
  );
}

// Saved cleaning steps per file type: pre-applied (as a preview) to the next file of that type.
export default function SavedSteps({ meta, onToast }) {
  const { role } = useRole();
  const canEdit = ["it_head", "coo"].includes(role?.id);
  const [steps, setSteps] = useState(null);
  const load = useCallback(() => listSteps().then(r => setSteps(r.steps)).catch(() => setSteps([])), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="tile" data-testid="saved-steps">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2"><Bookmark size={13} className="text-primary" /><span className="kpi-label flex-1">Saved cleaning steps per file type</span>
        <span className="text-[11px] text-muted">Pre-applied as a preview to the next file of that type — never promoted automatically</span></div>
      <div className="p-4 space-y-3">
        {steps === null && <div className="text-[12px] text-muted">Loading…</div>}
        {steps?.length === 0 && <div className="text-[12px] text-muted" data-testid="saved-steps-empty">No saved steps yet. In a review, choose “Save steps for …” to keep the rules and approved fixes for that file type.</div>}
        {steps?.map(s => <StepCard key={`${s.type}-${s.saved_at}`} s={s} meta={meta} canEdit={canEdit} onSaved={load} onToast={onToast} />)}
      </div>
    </div>
  );
}
